import { inject, Injectable } from '@angular/core';
import { AuthResult } from '@ionic-enterprise/auth';

import { CONSTANTS } from '../../../constants';
import { TokenResponse } from '../../../models';
import { AUTH_SESSION_STORAGE_TYPE, StorageAdapter, StorageType } from '../../../services';
import { UserData } from '../../user';

/**
 * Manages storage of authentication session data and user profile information.
 * Supports dynamic storage type switching (e.g. memory vs. persistent) for biometric flows.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private readonly storageAdapter = inject(StorageAdapter);
  private storageType = inject(AUTH_SESSION_STORAGE_TYPE);
  private storage = this.storageAdapter.get(this.storageType);

  /**
   * Retrieves the stored user data.
   * @returns The user data, or `null` if none has been saved yet.
   */
  public async getUserData(): Promise<UserData | null> {
    return this.storage.getValue<UserData>(CONSTANTS.USERDATA);
  }

  /**
   * Persists user data to the current storage.
   * @param userData - The user data to store.
   */
  public setUserData(userData: UserData): Promise<void> {
    return this.storage.setValue<UserData>(CONSTANTS.USERDATA, userData);
  }

  /**
   * Extracts user information from a raw auth result and saves it as {@link UserData}.
   * Merges with previously stored data to preserve fields absent from the token response.
   * @param authResult - The authentication result returned by the auth provider.
   * @throws {Error} If `username` or `userSecret` cannot be resolved from the token or prior storage.
   */
  public async saveUserDataFromAuthResult(authResult: AuthResult): Promise<void> {
    const tokenResponse: TokenResponse = JSON.parse(authResult.rawResult);
    const previousUserData = await this.getUserData();

    const hasSecondFactor = tokenResponse.haspin ?? previousUserData?.hasSecondFactor ?? false;
    const username = tokenResponse.userName ?? previousUserData?.username;
    const userSecret = tokenResponse.sec ?? previousUserData?.userSecret;

    if (!username) {
      throw new Error('Username is missing');
    }

    if (!userSecret) {
      throw new Error('UserSecret is missing');
    }

    const userData: UserData = {
      hasSecondFactor,
      username,
      userSecret,
    };

    return this.setUserData(userData);
  }

  /**
   * Retrieves the stored auth result.
   * @returns The auth result, or `null` if no session has been persisted.
   */
  public async getAuthResult(): Promise<AuthResult | null> {
    return this.storage.getValue<AuthResult | null>(CONSTANTS.AUTH_RESULT);
  }

  /**
   * Persists the auth result to the current storage.
   * @param authResult - The auth result to store.
   */
  public async setAuthResult(authResult: AuthResult): Promise<void> {
    await this.storage.setValue<AuthResult>(CONSTANTS.AUTH_RESULT, authResult);
  }

  /**
   * Removes all session-related entries (user data and auth result) from the current storage.
   */
  public async clear(): Promise<void> {
    await this.storage.removeValue(CONSTANTS.USERDATA);
    await this.storage.removeValue(CONSTANTS.AUTH_RESULT);
  }

  /**
   * Migrates all session data to a different storage backend without data loss.
   * The instance is only switched to the new storage after all writes succeed,
   * so it remains consistent and recoverable if a write fails midway.
   * Triggered when biometric settings change.
   * @param targetStorageType - The storage type to migrate to.
   */
  public async changeStorageType(targetStorageType: StorageType): Promise<void> {
    const userData = await this.getUserData();
    const session = await this.getAuthResult();

    const targetStorage = this.storageAdapter.get(targetStorageType);

    if (userData) {
      await targetStorage.setValue<UserData>(CONSTANTS.USERDATA, userData);
    }

    if (session) {
      await targetStorage.setValue<AuthResult>(CONSTANTS.AUTH_RESULT, session);
    }

    // All writes succeeded — switch the instance and clean up the old storage.
    const previousStorage = this.storage;
    this.storageType = targetStorageType;
    this.storage = targetStorage;

    await previousStorage.removeValue(CONSTANTS.USERDATA);
    await previousStorage.removeValue(CONSTANTS.AUTH_RESULT);
  }
}
